import { createPayloadToAssignDriver } from "../../mocks/express/response.transforms";
import { ClubbingSuggestions } from "../../utils/utiles.types";
import { checkIfInputIsValidObjectWithKeys, isInputAnArray } from "utils/helpers.utils";
import { Order } from "mocks/SharedTransform/interface";
import { addNRPageAction } from "utils/newRelicUtils";
import AppConstants from "../../constants";
import { TextConstants } from "constants/TextConstants";
import { TrackAssignActionParams } from "./ClubbingSuggestion.types";


const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

interface AssignClubbedOrdersParams {
  clubbingSuggestions: ClubbingSuggestions | undefined;
  selectedDriverId: string;
  consignment: Order;
  selectedConsignmentCodes: string[];
  storeId: string;
  assignDriverManually: (payload: any) => Promise<any>;
  removePrimaryDriver: (payload: any) => Promise<any>;
  rtdBreached?: boolean;
  assignments?: number;
}

const processOrdersSequentially = async <T>(
  items: T[],
  processFn: (item: T) => Promise<void>,
  delayMs = 3000
): Promise<void> => {
  for (let i = 0; i < items.length; i++) {
    await processFn(items[i]);
    if (i < items.length - 1) {
      await delay(delayMs);
    }
  }
};

const isValidClubbingSuggestions = (
  clubbingSuggestions: ClubbingSuggestions | undefined
): clubbingSuggestions is ClubbingSuggestions => {
  return Boolean(
    clubbingSuggestions &&
    checkIfInputIsValidObjectWithKeys(clubbingSuggestions) &&
    isInputAnArray(clubbingSuggestions.orders)
  );
};

/**
 * Tracks the Assign or Assign Clubbed action in New Relic.
 * 
 * @param actionType - The type of action ("Assign" or "Assign-Clubbed").
 * @param ignoredIds - Array of IDs that were ignored.
 * @param clubbedIds - Array of IDs that were selected.
 * @param clubbedCount - Total number of selected clubbing suggestions.
 * @param storeId - The store ID.
 * @param selectedConsignmentId - The consignment ID for which the action is performed.
 * @param readyState - Indicates if the order is in a ready state i.e true if order status is not one of restricted statuses.
 */
export const trackAssignAction = ({
  actionType,
  ignoredIds,
  clubbedIds,
  clubbedCount,
  storeId,
  selectedConsignmentId,
  readyState,
  timerPassed,
  clubbingVehicle,
  activityType,
  driverId,
  assignments,
}: TrackAssignActionParams) => {
  const ignoredCount = ignoredIds?.length || 0; // Handle case where ignoredIds might be undefined

  addNRPageAction(
    AppConstants.NR_FEATURES.CLUBBING_SUGGESTION.NAME,
    AppConstants.NR_FEATURES.CLUBBING_SUGGESTION.FEATURES.CLUBBING_SUGGESTION_RESPONSE,
    {
      actionType,
      clubbedCount,
      ignoredCount,
      clubbedIds,
      ignoredIds,
      storeId,
      selectedConsignmentId,
      readyState,
      timerPassed,
      clubbingVehicle,
      activityType,
      driverId,
      assignments,
    }
  );
};

//TODO: For the pilot release, calling the API in a loop. However, refactor this function post-pilot to handle via API.
export const assignClubbedOrders = async ({
  clubbingSuggestions,
  selectedDriverId,
  consignment,
  selectedConsignmentCodes,
  storeId,
  assignDriverManually,
  removePrimaryDriver,
  rtdBreached,
  assignments
}: AssignClubbedOrdersParams): Promise<void> => {
  if (!isValidClubbingSuggestions(clubbingSuggestions)) {
    return;
  }

  // Filter orders based on selected consignment codes
  const selectedOrders = clubbingSuggestions?.orders?.filter(
    (order: any) => selectedConsignmentCodes.includes(order?.consignmentCode)
  );

  if (!selectedOrders || selectedOrders.length === 0) {
    return;
  }

  const ordersToUnassign = selectedOrders.filter(
    (order: any) => order.assignee?.id === selectedDriverId
  );
  
  // Use selectedConsignmentCodes directly (already filtered and in correct sequence)
  if (selectedConsignmentCodes.length === 0) {
    return;
  }
   
  // Ensure at least 2 consignment codes are selected
  if (selectedConsignmentCodes.length < 2 && !(rtdBreached && clubbingSuggestions?.orders.length === 1)) {
    throw new Error(TextConstants.CLUBBING_SUGGESTION_ERROR);
  }
  // Track the "Assign Clubbed" action in New Relic
  const clubbedIds = selectedConsignmentCodes; // IDs of selected orders
  const ignoredIds = clubbingSuggestions.orders
    .filter(
      (order: any) => !selectedConsignmentCodes.includes(order.consignmentCode)
    )
    .map((order: any) => order.consignmentCode); // IDs of ignored suggestion
  const clubbedCount = clubbedIds.length; // Total number of selected IDs
  trackAssignAction({
    actionType: "Assign-Clubbed",
    ignoredIds,
    clubbedIds,
    clubbedCount,
    storeId,
    selectedConsignmentId: consignment?.consignmentCode,
    readyState: true, // Consider ready if at least 1 order is selected
    timerPassed: rtdBreached,
    clubbingVehicle: clubbingSuggestions?.vehicleType,
    activityType: consignment?.metadata?.activity,
    driverId: selectedDriverId,
    assignments,
  });
  
  // Unassign orders already assigned to selectedDriverId
  if (ordersToUnassign.length > 0) {
    await processOrdersSequentially(ordersToUnassign, async (order) => {
      const payload = {
        consignmentCode: order?.consignmentCode,
        driverIds: [selectedDriverId]
      };
      
      try {
        await removePrimaryDriver(payload);
      } catch (error: any) {
        console.error(`Failed to unassign ${order.consignmentCode}:`, error?.message || 'Unassignment failed');
      }
    });

    await delay(3000);
  }
  
  // Assign only selected orders
  await processOrdersSequentially(selectedConsignmentCodes, async (consignmentCode) => {
    const payload = createPayloadToAssignDriver([selectedDriverId], { 
      ...consignment, 
      consignmentCode
    });
    
    try {
      await assignDriverManually(payload);
    } catch (error: any) {
      console.error(`Failed to assign ${consignmentCode}:`, error?.message || 'Assignment failed');
    }
  });
};




